import type { GenParams, Question, Rng } from './types';
import { generate } from './generate';
import { pointsFor, type ScoringConfig } from './scoring';

export type Phase = 'question' | 'feedback';

export interface Session {
  phase: Phase;
  question: Question;
  /** index the player chose in the current feedback phase, or null */
  picked: number | null;
  score: number;
  solved: number;
  answered: number;
  streak: number;
  errors: number;
}

export interface SessionRules {
  gen: GenParams;
  scoring: ScoringConfig;
}

export function startSession(rules: SessionRules, rng: Rng): Session {
  return { phase: 'question', question: generate(rules.gen, rng), picked: null, score: 0, solved: 0, answered: 0, streak: 0, errors: 0 };
}

/** Pure transition: answering while not in 'question' phase is a no-op. A round never ends by itself; the player quits. */
export function answer(s: Session, index: number, rules: SessionRules): Session {
  if (s.phase !== 'question' || index < 0 || index >= s.question.options.length) return s;
  const correct = index === s.question.correct;
  const streak = correct ? s.streak + 1 : 0;
  const errors = s.errors + (correct ? 0 : 1);
  return {
    ...s,
    phase: 'feedback',
    picked: index,
    streak,
    errors,
    answered: s.answered + 1,
    solved: s.solved + (correct ? 1 : 0),
    score: s.score + (correct ? pointsFor(s.question.terms.length, streak, rules.scoring) : 0),
  };
}

/** Advance from feedback to the next question. */
export function next(s: Session, rules: SessionRules, rng: Rng): Session {
  if (s.phase !== 'feedback') return s;
  return { ...s, phase: 'question', question: generate(rules.gen, rng), picked: null };
}

export const accuracy = (s: Session): number => (s.answered ? Math.round((100 * s.solved) / s.answered) : 0);
